import type { ComponentType } from 'react';
import { IconBall, IconGrid, IconTicket, IconUser, IconWallet, type IconProps } from '@edil/ui';
import type { MessageKey } from '@edil/i18n';

/**
 * Navigation registry: the one place a feature becomes reachable. Sidebar (desktop), bottom bar
 * (mobile) and account menu render from this list, so a new feature such as deposits, limits or
 * loyalty is one entry here plus its screen in src/features.
 */
export type NavSection = 'play' | 'money' | 'account';
export type Currency = 'ETB' | 'POINTS';

export interface NavItem {
  id: string;
  href: string;
  label: MessageKey;
  icon: ComponentType<IconProps>;
  section: NavSection;
  /** Shown in the mobile bottom bar (keep to 4–5). */
  primary?: boolean;
  requiresAuth?: boolean;
  /** Only in these currencies (POINTS = FREE_PLAY mode, ETB = real money / demo). */
  currencies?: Currency[];
  /** Extra path prefixes that mark this item active. */
  matches?: string[];
  /** Only while this feature flag is on for players. */
  feature?: 'bingo';
}

export const NAV_ITEMS: NavItem[] = [
  // One entry per game; each opens that game's rooms.
  // Bingo is the home page (/); with bingo off, / sends players on to the raffle rooms.
  { id: 'bingo', href: '/', label: 'nav.bingo', icon: IconBall, section: 'play', primary: true, feature: 'bingo', matches: ['/bingo'] },
  { id: 'lobby', href: '/raffle/', label: 'nav.raffle', icon: IconGrid, section: 'play', primary: true, matches: ['/room', '/round', '/verify'] },
  { id: 'tickets', href: '/tickets/', label: 'nav.tickets', icon: IconTicket, section: 'play', primary: true, requiresAuth: true },
  { id: 'wallet', href: '/wallet/', label: 'nav.wallet', icon: IconWallet, section: 'money', primary: true, requiresAuth: true },
  { id: 'profile', href: '/profile/', label: 'nav.profile', icon: IconUser, section: 'account', primary: true, requiresAuth: true },
];

export const NAV_SECTIONS: { id: NavSection; label: MessageKey }[] = [
  { id: 'play', label: 'nav.section.play' },
  { id: 'money', label: 'nav.section.money' },
  { id: 'account', label: 'nav.section.account' },
];

export function isActive(item: NavItem, path: string): boolean {
  const norm = path.endsWith('/') ? path : `${path}/`;
  if (item.href === '/') return norm === '/' || (item.matches ?? []).some((m) => norm.startsWith(`${m}/`));
  return norm.startsWith(item.href) || (item.matches ?? []).some((m) => norm.startsWith(`${m}/`));
}

export function visibleItems(opts: { currency: Currency; signedIn: boolean; features?: { bingo?: boolean } }): NavItem[] {
  return NAV_ITEMS.filter((i) => (!i.currencies || i.currencies.includes(opts.currency)) && (!i.feature || opts.features?.[i.feature]));
}
